// MySchedulePage.jsx — full-calendar month/week view of the staff member's
// published shifts. Standalone (/my-schedule) or embedded inside an
// attendance tab (pass embedded={true}).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Calendar as CalIcon, Clock,
  X, ArrowLeftRight, Users,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON  = "#00FF99";
const AMBER = "#EF9F27";
const BLUE  = "#378ADD";
const PURP  = "#7F77DD";
const RED   = "#E24B4A";

const DOW_FULL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TYPE_STYLE = {
  morning:   { bg: "rgba(55,138,221,0.15)",  fg: BLUE,  border: "rgba(55,138,221,0.30)",  label: "Morning"   },
  afternoon: { bg: "rgba(239,159,39,0.15)",  fg: AMBER, border: "rgba(239,159,39,0.30)",  label: "Afternoon" },
  night:     { bg: "rgba(127,119,221,0.15)", fg: PURP,  border: "rgba(127,119,221,0.30)", label: "Night"     },
  oncall:    { bg: "rgba(85,85,85,0.20)",    fg: "#888",border: "rgba(85,85,85,0.45)",    label: "On-call"   },
  leave:     { bg: "rgba(226,75,74,0.10)",   fg: RED,   border: "rgba(226,75,74,0.35)",   label: "On leave"  },
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// ── Date utils (all in ISO YYYY-MM-DD strings to avoid TZ pitfalls) ─────
const isoOf = (d) => {
  const x = new Date(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const isoToday = () => isoOf(new Date());
const parseIso = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (iso, n) => {
  const d = parseIso(iso); d.setDate(d.getDate() + n);
  return isoOf(d);
};
const startOfMonth = (year, month0) => isoOf(new Date(year, month0, 1));
const endOfMonth   = (year, month0) => isoOf(new Date(year, month0 + 1, 0));
const startOfWeekMonday = (iso) => {
  const d = parseIso(iso);
  const dow = (d.getDay() + 6) % 7; // 0 = Mon
  d.setDate(d.getDate() - dow);
  return isoOf(d);
};

// hours between two HH:MM strings (handles night shift wrap-around)
const shiftHours = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  return mins / 60;
};

export default function MySchedulePage({ embedded = false }) {
  const navigate = useNavigate();

  // ── State
  const today = new Date();
  const [view,        setView]        = useState("month"); // month | week
  const [cursorYear,  setCursorYear]  = useState(today.getFullYear());
  const [cursorMonth, setCursorMonth] = useState(today.getMonth()); // 0-indexed
  const [weekAnchor,  setWeekAnchor]  = useState(startOfWeekMonday(isoToday()));
  const [shifts,      setShifts]      = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [drawer,      setDrawer]      = useState(null);     // {date}
  const [teamLookup,  setTeamLookup]  = useState({});       // {date: [{name, shift_type}]}

  // ── Compute fetch range (cover month grid: 6 weeks max)
  const { fromDate, toDate } = useMemo(() => {
    if (view === "week") {
      return { fromDate: weekAnchor, toDate: addDays(weekAnchor, 6) };
    }
    const monthStart = startOfMonth(cursorYear, cursorMonth);
    const monthEnd   = endOfMonth(cursorYear, cursorMonth);
    const gridStart  = startOfWeekMonday(monthStart);
    const gridEnd    = addDays(startOfWeekMonday(monthEnd), 6 * 7 - 1);
    return { fromDate: gridStart, toDate: gridEnd };
  }, [view, cursorYear, cursorMonth, weekAnchor]);

  // ── Load
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_BASE}/api/rota/my-schedule?from_date=${fromDate}&to_date=${toDate}`;
      const r = await fetch(url, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setShifts(Array.isArray(d.shifts) ? d.shifts : []);
      }
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  // ── Index shifts by date for fast lookup
  const shiftByDate = useMemo(() => {
    const m = {};
    for (const s of shifts) m[s.shift_date] = s;
    return m;
  }, [shifts]);

  // ── Month grid: array of weeks, each week is 7 ISO dates
  const monthGrid = useMemo(() => {
    const gridStart = startOfWeekMonday(startOfMonth(cursorYear, cursorMonth));
    const weeks = [];
    for (let w = 0; w < 6; w++) {
      const row = [];
      for (let d = 0; d < 7; d++) row.push(addDays(gridStart, w * 7 + d));
      weeks.push(row);
      // Stop early if the row's Monday is already past month end
      const lastDayOfMonth = endOfMonth(cursorYear, cursorMonth);
      if (row[0] > lastDayOfMonth) { weeks.pop(); break; }
    }
    return weeks;
  }, [cursorYear, cursorMonth]);

  const weekGrid = useMemo(() => {
    const row = [];
    for (let d = 0; d < 7; d++) row.push(addDays(weekAnchor, d));
    return row;
  }, [weekAnchor]);

  // ── Summary stats (within the visible month, not the grid padding)
  const stats = useMemo(() => {
    const monthStartIso = startOfMonth(cursorYear, cursorMonth);
    const monthEndIso   = endOfMonth(cursorYear, cursorMonth);
    const inMonth = shifts.filter((s) => s.shift_date >= monthStartIso && s.shift_date <= monthEndIso);
    let shiftCount = 0, hours = 0, offCount = 0, leaveCount = 0;
    for (const s of inMonth) {
      if (s.leave_status === "approved") { leaveCount++; continue; }
      if (s.is_off_day || s.shift_type === "off") { offCount++; continue; }
      shiftCount++;
      hours += shiftHours(s.start_time, s.end_time);
    }
    return {
      shiftCount,
      hours: Math.round(hours * 10) / 10,
      offCount,
      leaveCount,
    };
  }, [shifts, cursorYear, cursorMonth]);

  // ── Nav
  const prevMonth = () => {
    let m = cursorMonth - 1, y = cursorYear;
    if (m < 0) { m = 11; y--; }
    setCursorMonth(m); setCursorYear(y);
  };
  const nextMonth = () => {
    let m = cursorMonth + 1, y = cursorYear;
    if (m > 11) { m = 0; y++; }
    setCursorMonth(m); setCursorYear(y);
  };
  const prevWeek = () => setWeekAnchor(addDays(weekAnchor, -7));
  const nextWeek = () => setWeekAnchor(addDays(weekAnchor, 7));

  // ── Drawer team-lookup (fetched on demand)
  const openDrawer = async (iso) => {
    setDrawer({ date: iso });
    if (teamLookup[iso] !== undefined) return;
    try {
      const ws = startOfWeekMonday(iso);
      const r = await fetch(`${API_BASE}/api/rota/team-schedule?week_start=${ws}`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        const lookup = {};
        for (let i = 0; i < 7; i++) lookup[addDays(ws, i)] = [];
        for (const m of (d.members || [])) {
          for (const s of (m.shifts || [])) {
            if (!lookup[s.shift_date]) lookup[s.shift_date] = [];
            lookup[s.shift_date].push({ name: m.user_name, type: s.shift_type, is_off: s.is_off_day });
          }
        }
        setTeamLookup((t) => ({ ...t, ...lookup }));
      }
    } catch {}
  };

  // ── Render helpers
  const monthLabel = new Date(cursorYear, cursorMonth, 1)
    .toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const weekLabel = (() => {
    const start = parseIso(weekAnchor);
    const end = parseIso(addDays(weekAnchor, 6));
    return `${start.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${end.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
  })();

  const inner = (
    <div className={embedded ? "" : "p-6 space-y-5 text-white"}>
      {/* TOP BAR */}
      {!embedded && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "rgba(0,255,153,0.1)", color: NEON,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: `0.5px solid ${NEON}33`,
          }}>
            <CalIcon size={16} />
          </div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0, color: "#fff" }}>My Schedule</h1>
          <div style={{ flex: 1 }} />
          <button onClick={() => navigate("/my-schedule/swaps")} style={btnOutline}>
            <ArrowLeftRight size={12} /> Swap requests
          </button>
        </div>
      )}

      <div style={{
        display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button onClick={view === "month" ? prevMonth : prevWeek} style={iconBtn}>
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontSize: 14, color: "#fff", fontWeight: 500, minWidth: 200, textAlign: "center" }}>
            {view === "month" ? monthLabel : weekLabel}
          </span>
          <button onClick={view === "month" ? nextMonth : nextWeek} style={iconBtn}>
            <ChevronRight size={14} />
          </button>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: "inline-flex", borderRadius: 8, border: "0.5px solid #2a2a2a", overflow: "hidden" }}>
          {["week", "month"].map((v) => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: "5px 14px", fontSize: 11, textTransform: "capitalize",
              background: view === v ? "rgba(0,255,153,0.1)" : "transparent",
              color: view === v ? NEON : "#888",
              border: "none", cursor: "pointer",
            }}>{v}</button>
          ))}
        </div>
      </div>

      {/* Day-of-week header */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4,
      }}>
        {DOW_FULL.map((d) => (
          <div key={d} style={{
            fontSize: 11, color: "#555", textTransform: "uppercase",
            letterSpacing: 0.5, padding: "4px 8px",
          }}>{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {(view === "month" ? monthGrid.flat() : weekGrid).map((iso) => {
          const inCurMonth = view === "week"
            ? true
            : (parseIso(iso).getMonth() === cursorMonth);
          const sh = shiftByDate[iso];
          const isToday = iso === isoToday();
          return (
            <DayCell
              key={iso}
              iso={iso}
              inCurMonth={inCurMonth}
              isToday={isToday}
              shift={sh}
              tall={view === "week"}
              onClick={() => sh && openDrawer(iso)}
            />
          );
        })}
      </div>

      {loading && <p style={{ color: "#555", fontSize: 11, marginTop: 8 }}>Loading…</p>}

      {/* Summary strip */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 16,
      }}>
        <StatCard label="Shifts this month"  value={stats.shiftCount}  color={NEON} />
        <StatCard label="Hours this month"   value={`${stats.hours}h`} color="#fff" />
        <StatCard label="Days off"           value={stats.offCount}    color="#555" />
        <StatCard label="On leave"           value={stats.leaveCount}  color={RED}  />
      </div>

      {/* Drawer */}
      {drawer && (
        <ShiftDrawer
          date={drawer.date}
          shift={shiftByDate[drawer.date]}
          teamLookup={teamLookup}
          onClose={() => setDrawer(null)}
          onSwap={() => navigate("/my-schedule/swaps")}
        />
      )}
    </div>
  );

  return embedded ? inner : <MessengerLayout>{inner}</MessengerLayout>;
}

// ── DayCell ─────────────────────────────────────────────────────────────
function DayCell({ iso, inCurMonth, isToday, shift, tall, onClick }) {
  const dayNum = parseIso(iso).getDate();
  const isLeave  = shift?.leave_status === "approved";
  const isPending= shift?.leave_status === "pending";
  const stype = isLeave ? "leave"
              : isPending ? "leave"  // amber-styled below
              : (shift && !shift.is_off_day && shift.shift_type !== "off") ? shift.shift_type : null;
  const style = stype ? TYPE_STYLE[stype] : null;

  const hasShift = !!shift && !shift.is_off_day;
  return (
    <button
      onClick={hasShift || isLeave ? onClick : undefined}
      disabled={!hasShift && !isLeave}
      style={{
        background: "#0d0d0d",
        border: "0.5px solid #1a1a1a",
        borderRadius: 6,
        minHeight: tall ? 120 : 80,
        padding: "6px 8px",
        textAlign: "left",
        cursor: (hasShift || isLeave) ? "pointer" : "default",
        opacity: inCurMonth ? 1 : 0.4,
        position: "relative",
        transition: "filter .12s",
      }}
      onMouseEnter={(e) => { if (hasShift || isLeave) e.currentTarget.style.filter = "brightness(1.2)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; }}
      title={hasShift || isLeave
        ? `${iso} · ${style?.label || ""}${shift?.start_time ? ` ${shift.start_time}-${shift.end_time}` : ""}`
        : iso}
    >
      <div style={{
        fontSize: 12,
        color: !inCurMonth ? "#333" : isToday ? "#000" : "#aaa",
        fontWeight: isToday ? 600 : 400,
        marginBottom: 4,
        display: "inline-block",
        ...(isToday && {
          background: NEON,
          borderRadius: 999,
          padding: "0 6px",
          minWidth: 18, textAlign: "center",
        }),
      }}>
        {dayNum}
      </div>

      {style && (
        <div style={{
          background: style.bg,
          color: style.fg,
          border: `0.5px solid ${style.border}`,
          borderRadius: 5,
          padding: "3px 6px",
          fontSize: 10,
          fontWeight: 500,
          lineHeight: 1.3,
          ...(isPending && {
            background: "rgba(239,159,39,0.10)",
            color: AMBER,
            borderStyle: "dashed",
            borderColor: `${AMBER}88`,
          }),
        }}>
          <div>{isPending ? "Leave pending" : style.label}</div>
          {!isLeave && !isPending && shift?.shift_type !== "oncall" && (
            <div style={{ fontSize: 9, opacity: 0.7, marginTop: 1 }}>
              {shift.start_time}–{shift.end_time}
            </div>
          )}
          {tall && shift?.notes && (
            <div style={{ fontSize: 9, opacity: 0.6, marginTop: 3, fontStyle: "italic" }}>
              {shift.notes}
            </div>
          )}
        </div>
      )}
    </button>
  );
}

// ── StatCard ────────────────────────────────────────────────────────────
function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: "#0d0d0d", border: "0.5px solid #1c1c1c",
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, color, marginTop: 4, fontFamily: "ui-monospace, monospace" }}>
        {value}
      </div>
    </div>
  );
}

// ── ShiftDrawer ─────────────────────────────────────────────────────────
function ShiftDrawer({ date, shift, teamLookup, onClose, onSwap }) {
  const isLeave = shift?.leave_status === "approved";
  const stype = isLeave ? "leave"
              : (shift && !shift.is_off_day) ? shift.shift_type : "off";
  const style = TYPE_STYLE[stype] || TYPE_STYLE.morning;
  const hours = shift && !shift.is_off_day ? shiftHours(shift.start_time, shift.end_time) : 0;
  const breakMins = 30;
  const netHours = Math.max(0, hours - breakMins / 60);
  const d = parseIso(date);
  const teamSame = (teamLookup[date] || [])
    .filter((m) => m.type === shift?.shift_type && !m.is_off)
    .map((m) => m.name)
    .slice(0, 4);

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, height: "100vh",
      width: 320, background: "#111", borderLeft: "1px solid #1c1c1c",
      zIndex: 80, padding: 20, overflowY: "auto",
      boxShadow: "-12px 0 28px rgba(0,0,0,0.6)",
    }}>
      <button onClick={onClose} style={{
        position: "absolute", top: 10, right: 10,
        background: "transparent", border: "none", color: "#888", cursor: "pointer",
      }}><X size={16} /></button>

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 22, color: "#fff", fontWeight: 500 }}>
          {d.toLocaleDateString(undefined, { day: "numeric", month: "long" })}
        </div>
        <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
          {d.toLocaleDateString(undefined, { weekday: "long", year: "numeric" })}
        </div>
      </div>

      {!shift && (
        <p style={{ color: "#666", fontSize: 12 }}>No published shift for this date.</p>
      )}

      {shift && (
        <>
          <div style={{
            padding: 12, borderRadius: 10,
            background: style.bg, border: `0.5px solid ${style.border}`,
          }}>
            <div style={{ display: "inline-block", padding: "2px 10px", borderRadius: 999,
                         background: `${style.fg}22`, color: style.fg, fontSize: 11, fontWeight: 600 }}>
              {style.label}
            </div>
            {!isLeave && !shift.is_off_day && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 16, color: "#fff", display: "flex", alignItems: "center", gap: 8 }}>
                  <Clock size={14} color={style.fg} /> {shift.start_time} → {shift.end_time}
                </div>
                <div style={{ fontSize: 11, color: "#aaa", marginTop: 6, lineHeight: 1.6 }}>
                  Duration: {hours.toFixed(1)} hours<br />
                  Break: {breakMins} mins<br />
                  Net hours: {netHours.toFixed(1)} hours
                </div>
              </div>
            )}
            {isLeave && (
              <div style={{ marginTop: 10, fontSize: 12, color: "#bbb", lineHeight: 1.6 }}>
                Leave status: <strong style={{ color: RED }}>Approved</strong><br />
                {shift.notes && <span style={{ color: "#888" }}>{shift.notes}</span>}
              </div>
            )}
          </div>

          {teamSame.length > 0 && !isLeave && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase",
                           letterSpacing: 0.5, marginBottom: 6,
                           display: "flex", alignItems: "center", gap: 6 }}>
                <Users size={11} /> Also on {style.label.toLowerCase()} shift
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {teamSame.map((n) => (
                  <li key={n} style={{ fontSize: 12, color: "#ddd", padding: "3px 0" }}>{n}</li>
                ))}
              </ul>
            </div>
          )}

          {shift.notes && !isLeave && (
            <div style={{ marginTop: 16, padding: 10, borderRadius: 8,
                         background: "rgba(255,255,255,0.02)", border: "0.5px solid #1c1c1c",
                         fontSize: 11, color: "#bbb", lineHeight: 1.5 }}>
              <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                Notes
              </div>
              {shift.notes}
            </div>
          )}

          {!isLeave && !shift.is_off_day && (
            <button onClick={onSwap} style={{
              display: "flex", alignItems: "center", gap: 6, marginTop: 16,
              width: "100%", justifyContent: "center",
              padding: "10px 14px", borderRadius: 8, fontSize: 12,
              background: "rgba(0,255,153,0.12)", border: `0.5px solid ${NEON}55`,
              color: NEON, cursor: "pointer", fontWeight: 500,
            }}>
              <ArrowLeftRight size={12} /> Request shift swap
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── Reusable style fragments ────────────────────────────────────────────
const iconBtn = {
  background: "transparent", border: "0.5px solid #2a2a2a",
  borderRadius: 8, padding: 4, color: "#888", cursor: "pointer",
};

const btnOutline = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "6px 12px", fontSize: 11, borderRadius: 8,
  border: `0.5px solid ${NEON}55`, background: "transparent",
  color: NEON, cursor: "pointer",
};
